import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { guardarDestino } from '@/lib/destino';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { urlFotoFicha } from '@/lib/foto';
import {
  extrairCodigo,
  validarInscricaoPresencial,
  carregarPainelPresencial,
  vagasDaModalidade,
  type PainelPresencial,
  type RetornoValidacao,
} from '@/lib/presencial';
import ScreenHeader from '@/components/ui/ScreenHeader';
import Card from '@/components/ui/Card';
import LeitorQR from '@/pages/portaria/components/LeitorQR';
import { iniciarAudio, tocarBipes, vibrar } from '@/pages/portaria/utils';
import ResultadoValidacaoCard from './components/ResultadoValidacaoCard';
import FolhaBuscar from './components/FolhaBuscar';
import FolhaCancelar from './components/FolhaCancelar';
import FilaPorSenha from './components/FilaPorSenha';

type Estado = 'carregando' | 'permitido' | 'negado' | 'nao-encontrado';

const INTERVALO_PAINEL_MS = 5_000;
const TEMPO_RESULTADO_MS = 4_000;

function hora(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Sao_Paulo',
  });
}

// Tela da direção: bipa o QR da inscrição presencial e dá a vaga na ordem da fila.
export default function ValidarPresencialPage() {
  const { eventoId } = useParams<{ eventoId: string }>();
  const navigate = useNavigate();
  const eventoIdNum = Number(eventoId);
  const { user, pronto } = useAuth();

  const [estado, setEstado] = useState<Estado>('carregando');
  const [eventoNome, setEventoNome] = useState('');
  const [painel, setPainel] = useState<PainelPresencial | null>(null);
  const [resultado, setResultado] = useState<RetornoValidacao | null>(null);
  const [fotoUrl, setFotoUrl] = useState<string | null>(null);
  const [erroCamera, setErroCamera] = useState<string | null>(null);
  const [erroTransitorio, setErroTransitorio] = useState<string | null>(null);
  const [iniciada, setIniciada] = useState(false);
  const [buscaAberto, setBuscaAberto] = useState(false);
  const [cancelarAberto, setCancelarAberto] = useState(false);
  const [aba, setAba] = useState<'senha' | 'qr'>('qr');
  const [modalidadeSenhaId, setModalidadeSenhaId] = useState<number | null>(null);
  const processandoRef = useRef(false);
  const ultimoCodigoRef = useRef<string | null>(null);
  const ultimoTempoRef = useRef(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const erroTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Acesso: só a direção do evento.
  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      const destino = `/gerenciar/${eventoId}/validar`;
      guardarDestino(destino);
      navigate(`/entrar?voltar=${encodeURIComponent(destino)}`, { replace: true });
      return;
    }
    let ativo = true;
    async function carregar() {
      try {
        if (Number.isNaN(eventoIdNum)) {
          if (ativo) setEstado('nao-encontrado');
          return;
        }
        const { data: pode, error } = await supabase.rpc('pode_editar_evento', {
          p_evento: eventoIdNum,
        });
        if (error) throw error;
        if (!pode) {
          if (ativo) setEstado('negado');
          return;
        }
        const { data: ev } = await supabase
          .from('eventos')
          .select('nome')
          .eq('id', eventoIdNum)
          .maybeSingle();
        if (ativo) {
          setEventoNome((ev as { nome?: string } | null)?.nome || '');
          setEstado('permitido');
        }
      } catch {
        if (ativo) setEstado('negado');
      }
    }
    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, eventoId, eventoIdNum, pronto, user?.id]);

  const atualizarPainel = useCallback(async () => {
    if (Number.isNaN(eventoIdNum)) return;
    try {
      const p = await carregarPainelPresencial(eventoIdNum);
      setPainel(p);
    } catch {
      /* sem sinal — o próximo tique tenta de novo */
    }
  }, [eventoIdNum]);

  // Números ao vivo: a cada 5 s.
  useEffect(() => {
    if (estado !== 'permitido') return;
    atualizarPainel();
    const timer = setInterval(atualizarPainel, INTERVALO_PAINEL_MS);
    return () => clearInterval(timer);
  }, [estado, atualizarPainel]);

  // Modalidade que entra pela senha da fila: a tela abre em "Fila pela senha".
  const usaSenhaNoEvento = (painel?.modalidades ?? []).some((m) => m.usa_senha);
  useEffect(() => {
    if (usaSenhaNoEvento) setAba('senha');
  }, [usaSenhaNoEvento]);
  useEffect(() => {
    if (modalidadeSenhaId == null && usaSenhaNoEvento) {
      const primeira = (painel?.modalidades ?? []).find((m) => m.usa_senha);
      if (primeira) setModalidadeSenhaId(primeira.id);
    }
  }, [modalidadeSenhaId, usaSenhaNoEvento, painel]);

  // Mantém a tela acesa enquanto a validação está aberta.
  useEffect(() => {
    let lock: { release?: () => Promise<void> } | null = null;
    async function adquirir() {
      try {
        const wl = navigator as Navigator & {
          wakeLock?: { request: (t: string) => Promise<{ release?: () => Promise<void> }> };
        };
        lock = await wl.wakeLock?.request('screen');
      } catch {
        /* aparelho sem suporte */
      }
    }
    adquirir();
    const aoVisivel = () => {
      if (document.visibilityState === 'visible') adquirir();
    };
    document.addEventListener('visibilitychange', aoVisivel);
    return () => {
      document.removeEventListener('visibilitychange', aoVisivel);
      lock?.release?.();
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (erroTimeoutRef.current) clearTimeout(erroTimeoutRef.current);
    };
  }, []);

  const exibirResultado = useCallback(
    async (r: RetornoValidacao) => {
      if (r.situacao === 'vaga') {
        tocarBipes(1, 880);
        vibrar(140);
      } else if (r.situacao === 'lista_espera') {
        tocarBipes(2, 660);
        vibrar([80, 50, 80]);
      } else if (r.situacao === 'ja_validada') {
        tocarBipes(1, 440);
        vibrar(80);
      } else if (r.situacao === 'sem_senha') {
        tocarBipes(1, 520);
        vibrar(80);
      } else {
        tocarBipes(2, 300);
        vibrar([130, 60, 130]);
      }

      const foto = await urlFotoFicha(r.foto_url);
      setFotoUrl(foto);
      setResultado(r);

      if (r.situacao === 'vaga' || r.situacao === 'lista_espera') atualizarPainel();

      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setResultado(null), TEMPO_RESULTADO_MS);
    },
    [atualizarPainel],
  );

  const processar = useCallback(
    async (texto: string) => {
      if (processandoRef.current) return;
      const codigo = extrairCodigo(texto);
      if (!codigo) {
        await exibirResultado({
          ok: false,
          situacao: 'nao_encontrada',
          ordem: null,
          posicao_espera: null,
          nome: null,
          idade: null,
          foto_url: null,
          grupo: null,
          modalidade: null,
          vagas_usadas: null,
          vagas_total: null,
          mensagem: 'QR não reconhecido.',
        });
        return;
      }
      const agora = Date.now();
      if (codigo === ultimoCodigoRef.current && agora - ultimoTempoRef.current < 4_000) return;
      ultimoCodigoRef.current = codigo;
      ultimoTempoRef.current = agora;

      processandoRef.current = true;
      try {
        const r = await validarInscricaoPresencial(codigo);
        await exibirResultado(r);
      } catch {
        setErroTransitorio('Sem sinal — tente de novo');
        if (erroTimeoutRef.current) clearTimeout(erroTimeoutRef.current);
        erroTimeoutRef.current = setTimeout(() => setErroTransitorio(null), 2_500);
      } finally {
        processandoRef.current = false;
      }
    },
    [exibirResultado],
  );

  function iniciar() {
    iniciarAudio();
    setIniciada(true);
  }

  if (estado === 'carregando') {
    return (
      <div className="min-h-screen bg-background-100">
        <div className="mx-auto w-full max-w-[640px] px-5 py-8">
          <div className="h-40 animate-pulse rounded-[18px] bg-background-200/70" />
          <div className="mt-4 h-72 animate-pulse rounded-[18px] bg-background-200/70" />
        </div>
      </div>
    );
  }

  if (estado === 'negado' || estado === 'nao-encontrado') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background-100 px-5 py-8">
        <Card className="w-full max-w-[420px] p-8 text-center">
          <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
            <i className="ri-shield-cross-line text-[40px]" aria-hidden />
          </span>
          <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
            {estado === 'negado' ? 'Sem acesso' : 'Evento não encontrado'}
          </h1>
          <p className="mt-2 text-[14px] text-foreground-600">
            {estado === 'negado'
              ? 'Só quem está na direção deste evento pode validar as inscrições.'
              : 'Este evento não existe ou não está mais disponível.'}
          </p>
        </Card>
      </div>
    );
  }

  const modalidades = painel?.modalidades ?? [];
  const ultimas = painel?.ultimas ?? [];
  const modalidadesSenha = modalidades.filter((m) => m.usa_senha);
  const temFilaSenha = modalidadesSenha.length > 0;
  const modalidadeSenha =
    modalidadesSenha.find((m) => m.id === modalidadeSenhaId) ?? modalidadesSenha[0] ?? null;

  return (
    <div className="min-h-screen bg-background-100 pb-16">
      <ScreenHeader titulo="Validar inscrições" subtitulo={eventoNome} voltar />

      <main className="mx-auto w-full max-w-[640px] px-5 pt-5">
        {temFilaSenha && (
          <div className="mb-4 flex gap-1 rounded-full bg-background-100 p-1">
            <button
              type="button"
              onClick={() => setAba('senha')}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
                aba === 'senha'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              Fila pela senha
            </button>
            <button
              type="button"
              onClick={() => setAba('qr')}
              className={`flex-1 cursor-pointer whitespace-nowrap rounded-full px-4 py-2 text-[14px] font-semibold transition-colors ${
                aba === 'qr'
                  ? 'bg-primary-500 text-white'
                  : 'text-foreground-600 hover:text-foreground-900'
              }`}
            >
              Bipar QR
            </button>
          </div>
        )}

        {temFilaSenha && aba === 'senha' ? (
          <div>
            {modalidadesSenha.length > 1 && (
              <div className="-mx-4 mb-4 overflow-x-auto px-4">
                <div className="flex gap-2 whitespace-nowrap">
                  {modalidadesSenha.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setModalidadeSenhaId(m.id)}
                      className={`shrink-0 cursor-pointer whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        modalidadeSenha?.id === m.id
                          ? 'bg-secondary-500 text-white'
                          : 'border border-background-300 bg-background-50 text-foreground-800 hover:bg-background-100'
                      }`}
                    >
                      {m.nome}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {modalidadeSenha && (
              <FilaPorSenha
                modalidadeId={modalidadeSenha.id}
                modalidadeNome={modalidadeSenha.nome}
              />
            )}
          </div>
        ) : (
          <>
        {/* Contadores ao vivo */}
        {modalidades.length > 0 && (
          <Card className="p-4">
            <h2 className="text-[15px] font-bold text-foreground-950">Vagas por modalidade</h2>
            <div className="mt-3 space-y-3">
              {modalidades.map((m) => (
                <div key={m.id}>
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-[15px] font-semibold text-foreground-900">
                      {m.nome}
                    </span>
                  </div>
                  <p className="mt-0.5 text-[13px] text-foreground-600">
                    Com vaga {m.com_vaga}
                    {(() => {
                      const total = vagasDaModalidade(m);
                      return total != null ? `/${total}` : '';
                    })()}{' '}
                    · Lista de espera {m.lista_espera} · Aguardando {m.aguardando}
                  </p>
                  {m.grupos.length > 0 && (
                    <div className="mt-1 space-y-0.5">
                      {m.grupos.map((g) => (
                        <p key={g.id} className="text-[12px] text-foreground-500">
                          {g.nome}: {g.com_vaga}
                          {g.vagas != null ? `/${g.vagas}` : ''} · {g.aguardando} aguardando
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Card>
        )}

        {/* Câmera */}
        <div className="relative mt-4 h-[340px] overflow-hidden rounded-[18px] bg-black">
          {!iniciada ? (
            <div className="flex h-full flex-col items-center justify-center gap-5 px-6 text-center">
              <p className="text-[14px] text-white/70">
                Toque para ligar a câmera e o som antes de começar a bipar.
              </p>
              <button
                type="button"
                onClick={iniciar}
                className="aperta cursor-pointer whitespace-nowrap rounded-[18px] bg-success-600 px-10 py-5 text-[18px] font-bold text-white"
              >
                Ligar câmera
              </button>
            </div>
          ) : erroCamera ? (
            <div className="flex h-full flex-col items-center justify-center gap-3 px-6 text-center">
              <i className="ri-camera-off-line text-4xl text-white/70" aria-hidden />
              <p className="text-[14px] text-white/70">
                Não foi possível abrir a câmera. Use a busca pelo nome.
              </p>
            </div>
          ) : (
            <LeitorQR
              ativo={!resultado}
              aoLer={processar}
              aoErroCamera={(msg) => setErroCamera(msg)}
            />
          )}

          {erroTransitorio && (
            <div className="absolute inset-x-0 top-0 z-20 bg-danger-600 px-4 py-2.5 text-center text-[14px] font-semibold text-white">
              {erroTransitorio}
            </div>
          )}
        </div>

        <div className="mt-3">
          <button
            type="button"
            onClick={() => {
              iniciarAudio();
              setBuscaAberto(true);
            }}
            className="aperta flex h-12 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-[18px] border border-background-300 bg-background-50 text-[15px] font-semibold text-foreground-800 transition-colors hover:bg-background-100"
          >
            <i className="ri-user-search-line text-lg" aria-hidden />
            Não leu o QR? Buscar pelo nome
          </button>
        </div>

        {/* Últimas bipagens */}
        {ultimas.length > 0 && (
          <Card className="mt-4 p-4">
            <h2 className="text-[15px] font-bold text-foreground-950">Últimas bipagens</h2>
            <div className="mt-2 divide-y divide-background-200/70">
              {ultimas.map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-3">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[13px] font-bold ${
                        u.situacao === 'vaga'
                          ? 'bg-success-100 text-success-700'
                          : 'bg-warning-100 text-warning-700'
                      }`}
                    >
                      {u.ordem != null ? u.ordem : '—'}
                    </span>
                    <div className="min-w-0">
                      <p className="truncate text-[14px] font-semibold text-foreground-900">
                        {u.nome || '—'}
                      </p>
                      {u.grupo && <p className="text-[12px] text-foreground-500">{u.grupo}</p>}
                    </div>
                  </div>
                  <span className="shrink-0 text-[12px] text-foreground-400">{hora(u.hora)}</span>
                </div>
              ))}
            </div>
          </Card>
        )}

        <button
          type="button"
          onClick={() => setCancelarAberto(true)}
          className="mt-5 block w-full cursor-pointer whitespace-nowrap py-2 text-center text-[13px] font-semibold text-foreground-500 transition-colors hover:text-danger-600"
        >
          Cancelar quem não foi bipado
        </button>
          </>
        )}
      </main>

      {resultado && (
        <ResultadoValidacaoCard
          resultado={resultado}
          fotoUrl={fotoUrl}
          aoFechar={() => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            setResultado(null);
          }}
        />
      )}

      <FolhaBuscar
        eventoId={eventoIdNum}
        aberto={buscaAberto}
        aoValidar={(codigo) => {
          setBuscaAberto(false);
          processar(codigo);
        }}
        aoFechar={() => setBuscaAberto(false)}
      />

      <FolhaCancelar
        aberto={cancelarAberto}
        modalidades={modalidades}
        aoConcluir={atualizarPainel}
        aoFechar={() => setCancelarAberto(false)}
      />
    </div>
  );
}